import React from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../comman/modal.js";
import Button from "../comman/button.js";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation, getLocalizedValue } from "../../utils/translate.js";
import { DISH_IMAGE_PLACEHOLDER_HERO } from "../../utils/dishImagePlaceholder.js";
import { spiceApiKeyToTranslationKey } from "../../utils/dishFormUtils.js";

/**
 * DishInfoModal component - Modal displaying dish information similar to bychef.am
 * @param {Object} props - Component props
 * @param {Object} props.dish - Dish data
 * @param {Function} props.onClose - Close modal handler
 * @returns {JSX.Element} DishInfoModal component
 */
const DishInfoModal = ({ dish, onClose }) => {
    const navigate = useNavigate();
    const { language } = useLanguage();

    // Group additions by group - must be before early return
    const groupedAdditions = React.useMemo(() => {
        if (!dish || !dish.dishAdditionDtoList || dish.dishAdditionDtoList.length === 0) {
            return [];
        }

        const groups = dish.dishAdditionDtoList.reduce((acc, addition) => {
            const groupName = getLocalizedValue(addition.group, 'name', language) || getTranslation('menu.ungrouped', language) || 'Ungrouped';
            if (!acc[groupName]) {
                acc[groupName] = [];
            }
            acc[groupName].push(addition);
            return acc;
        }, {});

        return Object.entries(groups);
    }, [dish, language]);

    if (!dish) {
        return null;
    }

    const dishName = getLocalizedValue(dish, 'name', language);
    const dishIngredients = getLocalizedValue(dish, 'ingredient', language);
    const dishPortion = getLocalizedValue(dish, 'portion', language);

    const handleEditClick = () => {
        onClose();
        navigate(`/menu/edit/${dish.id}`);
    };

    // Build dish name with portion
    const fullDishName = dishPortion 
        ? `${dishName} (${dishPortion})` 
        : dishName;

    return (
        <Modal 
            isOpen={true} 
            onClose={onClose} 
            title={dishName}
            size="xl"
        >
            <div className="space-y-4">
                {/* Image */}
                <div className="w-full rounded-lg overflow-hidden bg-black -mx-6 -mt-4">
                    <img
                        key={dish.url} // Use URL as key to help browser cache
                        src={dish.url || DISH_IMAGE_PLACEHOLDER_HERO}
                        alt={dishName}
                        className="w-full h-[240px] object-contain transition-opacity duration-300"
                        onLoad={(e) => {
                            e.target.style.opacity = "1";
                        }}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = DISH_IMAGE_PLACEHOLDER_HERO;
                            e.target.style.opacity = "1";
                        }}
                        loading="eager" // Eager load since modal is immediately visible
                        decoding="async"
                        fetchpriority="low" // Low priority for modal images
                        style={{ opacity: 0 }}
                    />
                </div>

                {/* Badges */}
                <div className="flex flex-wrap gap-2">
                    {/* Dish Type Badge */}
                    {dish.type && (
                        <div className="inline-flex items-center gap-1.5 bg-white dark:bg-[#21262d] px-3 py-1 rounded-full border border-gray-200 dark:border-[#30363d]">
                            <span className="text-xs font-medium text-gray-700 dark:text-[#c9d1d9]">
                                {getTranslation(`dishTypes.${dish.type}`, language) || dish.type}
                            </span>
                        </div>
                    )}
                    
                    {/* Additions Status Badge */}
                    {groupedAdditions.length > 0 && (
                        <div className="inline-flex items-center gap-1.5 bg-white dark:bg-[#21262d] px-3 py-1 rounded-full border border-gray-200 dark:border-[#30363d]">
                            <span className="text-xs font-medium text-gray-700 dark:text-[#c9d1d9]">
                                {getTranslation('menu.hasAdditions', language) || 'With additions'}
                            </span>
                        </div>
                    )}
                </div>

                {/* Full Name with portion */}
                {dishPortion && (
                    <h3 className="text-lg font-bold text-gray-900 dark:text-[#c9d1d9]">
                        {fullDishName}
                    </h3>
                )}

                {/* Ingredients */}
                {dishIngredients && (
                    <div>
                        <p className="text-sm text-gray-600 dark:text-[#8b949e] leading-relaxed">
                            {dishIngredients}
                        </p>
                    </div>
                )}

                {/* Price */}
                {dish.price > 0 && (
                    <div className="border-t border-gray-200 dark:border-[#30363d] pt-4">
                        <p className="text-base font-bold text-gray-900 dark:text-[#c9d1d9]">
                            {dish.price} AMD
                        </p>
                    </div>
                )}

                {/* Additions Section - Grouped */}
                {groupedAdditions.length > 0 && (
                    <div className="border-t border-gray-200 dark:border-[#30363d] pt-4">
                        <h4 className="text-base font-bold text-gray-900 dark:text-[#c9d1d9] mb-3">
                            {getTranslation('menu.additions', language)}
                        </h4>
                        <div className="space-y-4">
                            {groupedAdditions.map(([groupName, additions], groupIndex) => (
                                <div key={groupIndex}>
                                    {/* Group Name */}
                                    <p className="text-sm font-semibold text-gray-700 dark:text-[#b1bac4] mb-2">
                                        {groupName}
                                    </p>
                                    {/* Group Additions */}
                                    <div className="space-y-2 ml-2">
                                        {additions.map((addition, index) => (
                                            <div 
                                                key={index}
                                                className="flex items-center justify-between text-sm"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <div className="w-4 h-4 rounded-full border-2 border-gray-400 dark:border-[#484f58]"></div>
                                                    <span className="text-gray-900 dark:text-[#c9d1d9]">
                                                        {getLocalizedValue(addition, 'name', language)}
                                                    </span>
                                                </div>
                                                {addition.price > 0 && (
                                                    <span className="text-gray-600 dark:text-[#8b949e] text-sm">
                                                        +{addition.overridedPrice !== null && addition.overridedPrice !== undefined 
                                                            ? addition.overridedPrice 
                                                            : addition.price} AMD
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Adjustable Spice Levels */}
                {dish.adjustableSpiceLevelDtoList && dish.adjustableSpiceLevelDtoList.length > 0 && (
                    <div className="border-t border-gray-200 dark:border-[#30363d] pt-4">
                        <h4 className="text-base font-bold text-gray-900 dark:text-[#c9d1d9] mb-3">
                            {getTranslation('menu.possibleSpiceLevels', language)}
                        </h4>
                        <div className="space-y-2 ml-2">
                            {dish.adjustableSpiceLevelDtoList.map((level, index) => {
                                const spiceLevelKey = spiceApiKeyToTranslationKey(level.spiceLevel);
                                return (
                                    <div 
                                        key={index}
                                        className="flex items-center gap-2 text-sm"
                                    >
                                        <div className="w-4 h-4 rounded-full border-2 border-gray-400 dark:border-[#484f58]"></div>
                                        <span className="text-gray-900 dark:text-[#c9d1d9]">
                                            {getTranslation(`spiceLevels.${spiceLevelKey}`, language) || level.spiceLevel}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Dietary Options */}
                {dish.dietaryOptionDtoList && dish.dietaryOptionDtoList.length > 0 && (
                    <div className="border-t border-gray-200 dark:border-[#30363d] pt-4">
                        <h4 className="text-base font-bold text-gray-900 dark:text-[#c9d1d9] mb-3">
                            {getTranslation('menu.dietaryOptions', language)}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                            {dish.dietaryOptionDtoList.map((option, index) => {
                                // Try to get translated name using the option's dietaryOptionValue property
                                const optionValue = option.dietaryOptionValue || option.type || option.name || '';
                                const translatedName = getTranslation(`dietaryOptions.${optionValue}`, language) 
                                    || getLocalizedValue(option, 'name', language)
                                    || optionValue;
                                
                                return (
                                    <span
                                        key={index}
                                        className="px-2.5 py-1 bg-white dark:bg-[#21262d] text-gray-700 dark:text-[#b1bac4] text-xs font-medium rounded-full border border-gray-200 dark:border-[#30363d]"
                                    >
                                        {translatedName}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Edit Button */}
                <div className="border-t border-gray-200 dark:border-[#30363d] pt-4">
                    <Button
                        variant="primary"
                        size="lg"
                        onClick={handleEditClick}
                        className="w-full"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        {getTranslation('common.edit', language)}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default DishInfoModal;
